import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {StyledConversationEntitySelector} from './style'
import {ConversationEntityType, NewConversationFormValidationSchema} from '../../types'
import {InputSelectProjectByPatient} from '@/features/projects/components/input-select-project-by-patient/InputSelectProjectByPatient'
import {useTranslation} from '@/translations/i18n'
import {ProjectByPatient} from '@/features/projects/types'
import {InputSelectTreatmentRequestByPatient} from '@/features/treatment-requests/components/input-select-treatment-request-by-patient/InputSelectTreatmentRequestByPatient'
import {TreatmentRequestSmall} from '@/features/treatment-requests/types'
import {InputSelectMedicalReportByPatient} from '@/features/medical-reports/components/input-select-medical-report-by-patient/InputSelectMedicalReportByPatient'

export const ConversationEntitySelector = ({patientId}: {patientId: string}) => {
    const {t} = useTranslation()
    const form = useFormContext<NewConversationFormValidationSchema>()
    const entityType = useWatch({control: form.control, name: 'entityType'})

    const renderEntitySelector = {
        project: (
            <Controller
                control={form.control}
                name="entity"
                render={({field}) => (
                    <InputSelectProjectByPatient
                        placeholder={t('conversations:select_reference_placeholder')}
                        patientId={patientId}
                        value={entityType === 'project' ? (field.value as ProjectByPatient) : undefined}
                        onClear={() => field.onChange(null)}
                        onChange={value =>
                            field.onChange({
                                id: value?.id,
                                code: value?.code,
                                startDate: value?.startDate,
                                expirationDate: value?.expirationDate
                            })
                        }
                    />
                )}
            />
        ),
        treatment_request: (
            <Controller
                control={form.control}
                name="entity"
                render={({field}) => (
                    <InputSelectTreatmentRequestByPatient
                        placeholder={t('conversations:select_reference_placeholder')}
                        patientId={patientId}
                        onChange={value => field.onChange(value)}
                        onClear={() => field.onChange(null)}
                        value={entityType === 'treatment_request' ? (field.value as TreatmentRequestSmall) : undefined}
                    />
                )}
            />
        ),
        medical_record_report: (
            <Controller
                control={form.control}
                name="entity"
                render={({field}) => (
                    <InputSelectMedicalReportByPatient
                        placeholder={t('conversations:select_reference_placeholder')}
                        patientId={patientId}
                        value={
                            entityType === 'medical_record_report' && field.value && 'name' in field.value
                                ? field.value
                                : undefined
                        }
                        onClear={() => field.onChange(null)}
                        onChange={value =>
                            field.onChange({
                                id: value?.id,
                                name: value?.medicalRecordReportType?.name
                            })
                        }
                    />
                )}
            />
        )
    } as const satisfies Record<NonNullable<ConversationEntityType>, React.ReactNode>

    if (!entityType) return null

    return <StyledConversationEntitySelector>{renderEntitySelector[entityType]}</StyledConversationEntitySelector>
}
